import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper, TextField, Drawer, FormControl, InputLabel, Select, MenuItem, IconButton } from '@mui/material';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Plus, ListChecks, X } from 'lucide-react';
import { useCampaignLists, useCreateCampaignList } from '@diggens/shared-hooks';
import { formatDate } from '@diggens/shared-utils';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function Lists() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListType, setNewListType] = useState<'static' | 'dynamic'>('static');

  const { data: lists, isLoading } = useCampaignLists();
  const createList = useCreateCampaignList();

  // Filter by search
  const filteredLists = (lists || []).filter((list) => !search || list.name.toLowerCase().includes(search.toLowerCase()));

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    await createList.mutateAsync({
      name: newListName.trim(),
      description: newListDescription.trim() || undefined,
      list_type: newListType,
    });
    setDrawerOpen(false);
    setNewListName('');
    setNewListDescription('');
    setNewListType('static');
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'list_type',
      headerName: 'Type',
      width: 120,
      valueFormatter: (value: string) => (value === 'dynamic' ? 'Dynamic' : 'Static'),
    },
    {
      field: 'active_count',
      headerName: 'Active',
      width: 100,
      type: 'number',
    },
    {
      field: 'suggested_count',
      headerName: 'Suggested',
      width: 100,
      type: 'number',
    },
    {
      field: 'total_count',
      headerName: 'Total',
      width: 100,
      type: 'number',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#10B981' }}>
            <ListChecks size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} />
          </Box>
          <Typography variant="h4">Campaign Lists</Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />} onClick={() => setDrawerOpen(true)}>
          Create List
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField fullWidth placeholder="Search lists..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 250px)' }}>
        <DataGrid
          rows={filteredLists}
          columns={columns}
          loading={isLoading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[25, 50, 100]}
          onRowClick={(params) => navigate(`${location.pathname}/${params.id}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 450, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Create List</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="List Name" value={newListName} onChange={(e) => setNewListName(e.target.value)} fullWidth autoFocus />
            <TextField label="Description" value={newListDescription} onChange={(e) => setNewListDescription(e.target.value)} fullWidth multiline rows={2} />
            <FormControl fullWidth>
              <InputLabel>List Type</InputLabel>
              <Select value={newListType} label="List Type" onChange={(e) => setNewListType(e.target.value as 'static' | 'dynamic')}>
                <MenuItem value="static">Static - Manual membership</MenuItem>
                <MenuItem value="dynamic">Dynamic - Criteria-based</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button variant="outlined" onClick={() => setDrawerOpen(false)} fullWidth>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleCreateList} disabled={!newListName.trim() || createList.isPending} fullWidth>
              Create
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
